import { APP_PATHS } from '@core/constants';
import { TagStateOf } from '@core/helpers';
import {
  activityStatusLabel,
  deviceActionLabel,
  deviceActivitySummary,
  scalarText,
} from '@core/helpers/activity-label.helper';
import { Activity, ActivityResponse } from '@core/models/activity.model';
import { DataSourceDevice } from '@core/models/installation.model';
import { TagState } from '@custom-components/general/tag/tag.component';
import { TranslateService } from '@ngx-translate/core';

type Translate = Pick<TranslateService, 'instant'>;

export const INSTALLATION_ACTIONS = [
  'installation_created',
  'installation_device_joined',
  'installation_details_updated',
  'installation_moved',
  'installation_timezone_changed',
  'installation_coordinates_set',
  'installation_profile_changed',
  'installation_binding_changed',
  'installation_binding_retracted',
  'installation_point_created',
  'installation_point_renamed',
  'installation_phase_count_set',
  'installation_phase_count_detected',
  'installation_migration_finding_acknowledged',
] as const;

const ACTION_ICONS: Readonly<Record<string, string>> = {
  installation_created: 'add_circle_outline',
  installation_device_joined: 'router',
  installation_details_updated: 'edit',
  installation_moved: 'moving',
  installation_timezone_changed: 'schedule',
  installation_coordinates_set: 'location_on',
  installation_profile_changed: 'tune',
  installation_binding_changed: 'link',
  installation_binding_retracted: 'link_off',
  installation_point_created: 'add_circle_outline',
  installation_point_renamed: 'drive_file_rename_outline',
  installation_phase_count_set: 'electrical_services',
  installation_phase_count_detected: 'electrical_services',
  installation_migration_finding_acknowledged: 'task_alt',
  clear_device_config: 'restart_alt',
  lan_connection_test: 'lan',
  update_settings: 'settings',
  pol: 'download',
  send_initial_config: 'upload_file',
  sim_connection_test: 'sim_card',
  verification: 'verified',
  timezone_config: 'schedule',
  send_device_config: 'upload_file',
  update_meter_role: 'swap_vert',
  firmware_update: 'system_update_alt',
  sim_topup: 'sim_card',
  sim_extension: 'sim_card',
  timezone_mismatch: 'schedule',
  timezone_defaulted: 'schedule',
};

export type ActivityRead = ActivityResponse | 'failed';

export interface ActivitySource {
  label: string;
  link: unknown[] | null;
}

export interface ActivityRow {
  source: string;
  sourceLink: unknown[] | null;
  action: string;
  message: string;
  status: string;
  statusState: TagState;
}

export function activityIcon(action: string): string {
  return ACTION_ICONS[action] ?? 'history';
}

export function isInstallationActivity(activity: Pick<Activity, 'scope'>): boolean {
  return activity.scope === 'installation';
}

export function installationActionLabel(translate: Translate, action: string): string {
  return (INSTALLATION_ACTIONS as readonly string[]).includes(action)
    ? translate.instant('installations.activity.actions.' + action)
    : action;
}

export function activityActionLabel(
  translate: Translate,
  activity: Pick<Activity, 'scope' | 'action'>,
): string {
  return isInstallationActivity(activity)
    ? installationActionLabel(translate, activity.action)
    : deviceActionLabel(translate, activity.action);
}

// A device row may come from a device that no longer feeds the installation, and the data sources
// name only the devices at its location, so such a device is named by its LDN alone.
export function activitySource(
  translate: Translate,
  activity: Pick<Activity, 'scope' | 'device_id' | 'device_ldn'>,
  devices: DataSourceDevice[],
): ActivitySource {
  if (isInstallationActivity(activity) || activity.device_id === null) {
    return { label: translate.instant('installations.activity.installationSource'), link: null };
  }
  const deviceId = activity.device_id;
  const ldn = activity.device_ldn ?? String(deviceId);
  const typeName = devices.find(device => device.deviceId === deviceId)?.deviceTypeName;
  return {
    label: typeName ? `${typeName} · ${ldn}` : ldn,
    link: ['/', APP_PATHS.Devices, deviceId, 'activity'],
  };
}

export function activitySummary(
  translate: Translate,
  activity: Pick<Activity, 'scope' | 'action' | 'message'>,
): string {
  if (!isInstallationActivity(activity)) return deviceActivitySummary(translate, activity);
  return activity.message ? installationMessageSummary(activity.message) : '';
}

export function activityRow(
  translate: Translate,
  activity: Activity,
  devices: DataSourceDevice[],
): ActivityRow {
  const source = activitySource(translate, activity, devices);
  return {
    source: source.label,
    sourceLink: source.link,
    action: activityActionLabel(translate, activity),
    message: activitySummary(translate, activity),
    status: activityStatusLabel(translate, activity.status),
    statusState: TagStateOf.activityStatus(activity.status),
  };
}

function isEmpty(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value as object).length === 0)
  );
}

function isChange(value: unknown): value is { old: unknown; new: unknown } {
  return typeof value === 'object' && value !== null && 'old' in value && 'new' in value;
}

function changeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '–';
  return scalarText(value);
}

const GUID = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
const IDENTIFIER_KEY = /(?:^|_)o?ids?$|[a-z](?:Oid|Id)s?$/;

function onlyIdentifies(value: unknown): boolean {
  if (typeof value === 'string') return GUID.test(value);
  if (Array.isArray(value)) return value.length > 0 && value.every(onlyIdentifies);
  if (isChange(value)) {
    return [value.old, value.new].every(side => isEmpty(side) || onlyIdentifies(side));
  }
  return false;
}

// The General tab's short list says what an activity changed, so it leaves out the fields that only
// point at something, such as a point's oid. Null when the message has nothing else.
export function messageWithoutIdentifiers(message: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(message);
  } catch {
    return message;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return message;
  const kept = Object.entries(parsed).filter(
    ([key, value]) => !isEmpty(value) && !IDENTIFIER_KEY.test(key) && !onlyIdentifies(value),
  );
  return kept.length ? JSON.stringify(Object.fromEntries(kept)) : null;
}

// A changed field is sent as {old, new}. Lists of codes or ids are spelled out; any other nested
// value shows only its name, and the message dialog has the rest. A message whose fields are all
// empty, such as a link that bound no point, says nothing here.
export function installationMessageSummary(message: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(message);
  } catch {
    return message;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return message;
  const parts = Object.entries(parsed)
    .filter(([, value]) => !isEmpty(value))
    .map(([key, value]) => {
      if (isChange(value)) return `${key}: ${changeValue(value.old)} → ${changeValue(value.new)}`;
      if (Array.isArray(value)) {
        return value.every(item => typeof item !== 'object') ? `${key}: ${value.join(', ')}` : key;
      }
      return typeof value === 'object' ? key : `${key}: ${scalarText(value)}`;
    });
  return parts.join(', ');
}
